import { Component, Input, OnInit, OnDestroy, Output, EventEmitter } from "@angular/core";
import { MatDialog } from "@angular/material/dialog";
import {
  DataListRelationship,
  DataListTagGroup,
} from "src/app/shared/models/data-list";
import { DocumentTag } from "src/app/shared/models/common-types";
import {
  CreateUpdateDisputePartyBaseDto,
  DisputeCaseBaseDto,
  DisputeDocumentBaseDto,
  DisputePartyBaseDto,
} from "src/app/shared/models/dispute-resolution/dispute-base-models";
import { DataListService } from "src/app/shared/services/data-list.service";
import { DisputeCaseFactoryService } from "src/app/shared/services/dispute-resolution/dispute-case-factory.service";
import { NotificationService } from "src/app/shared/services/notification.service";
import { DocumentService } from "src/app/shared/services/document.service";
import { TagPickerModalComponent } from "src/app/documentation/tag-picker-modal/tag-picker-modal.component";
import { AuthService } from "src/app/auth/auth.service";
import { RenameModalComponent } from "../../rename-modal/rename-modal.component";
import { forkJoin, Subscription } from "rxjs";
import { UserService } from "src/app/shared/services/user.service";

@Component({
  selector: "app-documents-list",
  templateUrl: "./documents-list.component.html",
  styleUrls: ["./documents-list.component.scss"],
  standalone: false,
})
export class DocumentsListComponent implements OnInit, OnDestroy {
  @Input() disputeCase: DisputeCaseBaseDto;
  @Input() documents: DisputeDocumentBaseDto[] = [];
  @Input() parties: CreateUpdateDisputePartyBaseDto[] = [];
  @Input() relationshipOptions: DataListRelationship[] = [];
  @Output() documentPartiesChange = new EventEmitter<
    CreateUpdateDisputePartyBaseDto[]
  >();

  mdParties: CreateUpdateDisputePartyBaseDto[] = [];

  displayedColumns: string[] = [
    "expand",
    "title",
    "displayText",
    "createdOn",
    "actions",
  ];

  isStateUser: boolean = false;
  availableTags: DataListTagGroup[] = [];

  private subscriptions = new Subscription();

  constructor(
    private dataListService: DataListService,
    private disputeCaseFactoryService: DisputeCaseFactoryService,
    private notificationService: NotificationService,
    private documentService: DocumentService,
    private authService: AuthService,
    private userService: UserService,
    private dialog: MatDialog
  ) { }

  ngOnInit(): void {
    this.isStateUser = this.authService.isStateEducationAgencyUser;

    this.parties = this.parties.filter((p) => !p.isChild);

    this.subscriptions.add(
      forkJoin({
        tagGroups: this.dataListService.getAllowedTagGroups(),
        mdUsers: this.userService.getUsersByRoleNameAndAdministrativeUnit("MD", this.disputeCase.administrativeUnitId),
      }).subscribe(({ tagGroups, mdUsers }) => {
      this.availableTags = tagGroups.filter(
        (tg) => tg.label === "Dispute Resolution"
      );


      // Filter out MD users that are already in the parties array (using userId)
      const filteredMdUsers = mdUsers.filter(u =>
        !this.parties.some(party => party.userId === u.id)
      );

      this.mdParties = filteredMdUsers.map(u => ({
        firstName: u.firstName,
        lastName: u.lastName,
        email: u.email,
        phoneNumber: u.phoneNumber,
        relationshipOther: 'Monitoring Director',
        userId: u.id,
      }));

      this.sortParties();
      })
    );
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }


  async onDelete(doc: DisputeDocumentBaseDto) {
    if (
      await this.notificationService.confirmationPromise(
        "Are you sure you want to delete? This cannot be undone.",
        "Delete Item"
      )
    ) {
      this.disputeCaseFactoryService
        .deleteDocument(this.disputeCase.disputeCaseType, doc.id)
        .subscribe({
          next: () => {
            this.refreshDocuments();

            // Find in parties the documentParties for this document and remove it
            this.parties.forEach((party) => {
              party.disputeDocumentParties =
                party.disputeDocumentParties?.filter(
                  (ddp) => ddp.disputeDocumentId !== doc.id
                );
            });

            // Also remove from MD parties
            this.mdParties.forEach((mdParty) => {
              mdParty.disputeDocumentParties =
                mdParty.disputeDocumentParties?.filter(
                  (ddp) => ddp.disputeDocumentId !== doc.id
                );
            });
            this.documentPartiesChange.emit(this.getPartiesWithAssignedDocuments());
            this.notificationService.success("Document deleted successfully.");
          },
          error: () => {
            this.notificationService.error("Failed to delete document.");
          },
        });
    }
  }

  editTags(doc: DisputeDocumentBaseDto) {
    const dialogRef = this.dialog.open(TagPickerModalComponent, {
      width: "600px",
      data: {
        availableTags: this.availableTags,
        preselectedTagIds: doc.document.documentTags?.map((t) => t.tagId) || [],
        isStateUser: this.isStateUser,
      },
      disableClose: true, // Prevent closing by clicking outside
    });
    dialogRef.afterClosed().subscribe({
      next: (result: string[] | undefined) => {
        if (result) {
          this.documentService.updateTags(doc.document.id, result).subscribe({
            next: () => {
              const existing = doc.document.documentTags ?? [];
              doc.document.documentTags = result.map(
                (tagId) =>
                  existing.find((t) => t.tagId === tagId) ??
                  ({ tagId } as DocumentTag)
              );
              this.notificationService.success(
                "Tags have been successfully modified."
              );
            },
            error: () => {
              this.notificationService.error("Failed to update tags.");
            },
          });
        }
      },
      error: () => {
        this.notificationService.error("Dialog closed with error.");
      },
    });
  }

  rename(doc: DisputeDocumentBaseDto) {
    const dialogRef = this.dialog.open(RenameModalComponent, {
      width: "300px",
      data: doc.document.title,
    });
    dialogRef.afterClosed().subscribe({
      next: (result) => {
        if (result) {
          this.documentService.rename(doc.document.id, result).subscribe({
            next: () => {
              this.notificationService.success(
                "Title has been successfully modified."
              );
              this.refreshDocuments();
            },
            error: () => {
              this.notificationService.error("Failed to rename document.");
            },
          });
        }
      },
      error: () => {
        this.notificationService.error("Dialog closed with error.");
      },
    });
  }

  downloadDocument(doc: DisputeDocumentBaseDto) {
    if (!doc.document?.id) {
      this.notificationService.error("Document ID not available.");
      return;
    }

    this.documentService.getDocument(doc.document.id).subscribe({
      next: (url) => {
        window.open(url);
      },
      error: () => {
        this.notificationService.error("Failed to download document.");
      },
    });
  }

  getRelationshipLabel(party: DisputePartyBaseDto): string | undefined {
    if (party.isFiler) {
      return "Filer";
    } else if (party.isRespondent) {
      return "Responder";
    } else if (
      !this.relationshipOptions ||
      this.relationshipOptions.length === 0
    ) {
      // Relationship options not loaded yet, check if there's relationshipOther
      return party.relationshipOther || undefined;
    } else {
      const relationship = this.relationshipOptions.find(
        (r) => r.id === party.relationshipId
      );
      if (relationship) {
        return (
          relationship.label +
          (party.relationshipOther ? ` (${party.relationshipOther})` : "")
        );
      } else if (party.relationshipOther) {
        // No relationship found but relationshipOther exists
        return party.relationshipOther;
      }
    }
  }

  toggleRow(row: Record<string, unknown>): void {
    row.expanded = !row.expanded;
  }

  onPartyCheckboxChange(
    document: DisputeDocumentBaseDto,
    party: CreateUpdateDisputePartyBaseDto,
    checked: boolean
  ): void {
    // Find the party in the appropriate array (original parties or MD parties)
    const isMdParty = this.mdParties.some(mdParty => mdParty.userId === party.userId);
    let targetParty: CreateUpdateDisputePartyBaseDto;

    if (isMdParty) {
      targetParty = this.mdParties.find(mdParty => mdParty.userId === party.userId)!;
    } else {
      targetParty = this.parties.find(p => p.id === party.id)!;
    }

    if (!targetParty.disputeDocumentParties) {
      targetParty.disputeDocumentParties = [];
    }

    if (checked) {
      // Add if not already present
      if (
        !targetParty.disputeDocumentParties.some(
          (ddp) => ddp.disputeDocumentId === document.id
        )
      ) {
        targetParty.disputeDocumentParties.push({
          disputeDocumentId: document.id,
          disputePartyId: targetParty.id,
        });
      }
    } else {
      // Remove if present
      targetParty.disputeDocumentParties = targetParty.disputeDocumentParties.filter(
        (ddp) => ddp.disputeDocumentId !== document.id
      );
    }

    // Emit the updated parties to notify parent component
    this.documentPartiesChange.emit(this.getPartiesWithAssignedDocuments());
  }

  isPartyChecked(
    document: DisputeDocumentBaseDto,
    party: CreateUpdateDisputePartyBaseDto
  ): boolean {
    // Check if this is an MD party
    const isMdParty = this.mdParties.some(mdParty => mdParty.userId === party.userId);
    let targetParty: CreateUpdateDisputePartyBaseDto;

    if (isMdParty) {
      targetParty = this.mdParties.find(mdParty => mdParty.userId === party.userId)!;
    } else {
      targetParty = this.parties.find(p => p.id === party.id)!;
    }

    return !!targetParty?.disputeDocumentParties?.some(
      (ddp) => ddp.disputeDocumentId === document.id
    );
  }

  getAllParties(): CreateUpdateDisputePartyBaseDto[] {
    // Filter out MD parties that are already in this.parties to avoid visual duplication
    const newMdParties = this.mdParties.filter(mdParty =>
      !this.parties.some(party => party.userId === mdParty.userId)
    );

    // Return all parties (original + new MD parties) for display purposes
    return [...this.parties, ...newMdParties];
  }

  private getPartiesWithAssignedDocuments(): CreateUpdateDisputePartyBaseDto[] {
    // Get MD parties that have documents assigned to them OR have an ID (exist in DB)
    const assignedMdParties = this.mdParties.filter(mdParty =>
      (mdParty.disputeDocumentParties && mdParty.disputeDocumentParties.length > 0) ||
      mdParty.id // Include if they have an ID (exist in database)
    );

    // Filter out MD parties that are already in this.parties to avoid duplicates
    const newMdParties = assignedMdParties.filter(mdParty =>
      !this.parties.some(party => party.userId === mdParty.userId)
    );

    // Filter original parties to exclude MD parties that have no documents assigned and no ID
    const filteredOriginalParties = this.parties.filter(party =>
      !party.userId || // Not an MD party (original parties)
      party.id || // Has an ID (exists in DB)
      (party.disputeDocumentParties && party.disputeDocumentParties.length > 0) // Has documents assigned
    );

    // Return filtered original parties plus new assigned MD parties (avoiding duplicates)
    return [...filteredOriginalParties, ...newMdParties];
  }

  private sortParties() {
    if (!this.parties || !this.relationshipOptions) return;

    const getLabel = (party: CreateUpdateDisputePartyBaseDto) => {
      if (party.isFiler) return "Filer";
      if (party.isRespondent) return "Responder";
      const rel = this.relationshipOptions.find(
        (r) => r.id === party.relationshipId
      );
      return rel ? rel.label : "";
    };

    this.parties = [
      ...this.parties.filter((p) => p.isFiler),
      ...this.parties.filter((p) => p.isRespondent && !p.isFiler),
      ...this.parties
        .filter((p) => !p.isFiler && !p.isRespondent)
        .sort((a, b) => getLabel(a).localeCompare(getLabel(b))),
    ];
  }

  reloadMdUsers() {
    this.userService.getUsersByRoleNameAndAdministrativeUnit("MD", this.disputeCase.administrativeUnitId)
      .subscribe((mdUsers) => {
        // Filter out MD users that are already in the parties array (using userId)
        const filteredMdUsers = mdUsers.filter(u =>
          !this.parties.some(party => party.userId === u.id)
        );

        this.mdParties = filteredMdUsers.map(u => ({
          firstName: u.firstName,
          lastName: u.lastName,
          email: u.email,
          phoneNumber: u.phoneNumber,
          relationshipOther: 'Monitoring Director',
          userId: u.id,
        }));
      });
  }

  private refreshDocuments() {
    this.disputeCaseFactoryService
      .getDocumentsByDisputeCase(
        this.disputeCase.disputeCaseType,
        this.disputeCase.id
      )
      .subscribe((documents) => {
        this.documents = (documents as DisputeDocumentBaseDto[]) || [];
      });
  }
}
